import { Direction } from '@angular/cdk/bidi';
import { MediaMatcher } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, DestroyRef, HostBinding, inject, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivationEnd, Event, NavigationStart, Router } from '@angular/router';
import { RouteData } from '@app/app.routing';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { ActivationEndService } from '@app/features/main/activation-end.service';
import { SidenavService } from '@app/features/main/sidenav.service';
import { AuthService } from '@auth0/auth0-angular';
import { TranslateService } from '@ngx-translate/core';
import packageJson from '@root/package.json';
import ApiService from '@services/api.service';
import IdleService from '@services/idle.service';
import { AccessManager } from '@services/managers/access.manager';
import { NotificationManager } from '@services/managers/notification.manager';
import { filter, first, switchMap } from 'rxjs/operators';

@Component({
    selector: 'mya-main',
    templateUrl: './main.component.html',
    standalone: false
})
export class MainComponent implements OnDestroy {
  public readonly sidenavService = inject(SidenavService);
  protected readonly accessManager = inject(AccessManager);

  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly media = inject(MediaMatcher);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly titleService = inject(Title);
  private readonly activationEndService = inject(ActivationEndService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly notificationManager = inject(NotificationManager);
  private readonly idleService = inject(IdleService);
  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  public mobileQuery: MediaQueryList;
  public _mobileQueryListener: () => void;

  public dir: Direction = 'ltr';

  public data: RouteData = <RouteData>{};

  public version: string;

  /**
   * Removes the top offset normally reserved for the menubar (see pages.scss's `mya-main` rule), since
   * there's no menubar to make room for on a hideMenubar route (e.g. a chart popup).
   */
  @HostBinding('class.no-menubar')
  public get noMenubar(): boolean {
    return this.data.hideMenubar;
  }

  constructor() {
    this.mobileQuery = this.media.matchMedia('(min-width: 768px)');
    this._mobileQueryListener = (): void => this.changeDetectorRef.detectChanges();
    this.mobileQuery.addEventListener('change', this._mobileQueryListener);

    this.version = this.translate.instant('VALUE.APP_VERSION', {'version': packageJson.version});

    /*
     Subscribe to all events thrown by the Angular router through the common service
     TODO: Find out how to centralise the pipe operations in the service while still allowing to reset the data
     */
    this.activationEndService.getRouteData()
      .pipe(
        filter((event: Event) => event instanceof NavigationStart),
        switchMap(() => {
          return this.router.events.pipe(
            filter((event: Event) => event instanceof ActivationEnd),
            filter((event: ActivationEnd) => event.snapshot.outlet === 'primary'),
            first()
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((activationEnd: ActivationEnd) => {
        // Make sure to overwrite the hideSidenav, hideHeader, closeSidenav and hideMenubar values with proper booleans
        this.data = <RouteData>{};
        this.data = {
          ...activationEnd.snapshot.data,
          hideHeader: !!activationEnd.snapshot.data.hideHeader,
          hideSidenav: !!activationEnd.snapshot.data.hideSidenav,
          closeSidenav: !!activationEnd.snapshot.data.closeSidenav,
          hideMenubar: !!activationEnd.snapshot.data.hideMenubar
        };

        // On mobile, scroll to top after navigating to new page
        if (this.mobileQuery.matches) {
          window.scrollTo(0, 0);
        }

        // If sidenav should be closed, we update its state
        if (this.data.closeSidenav) {
          this.sidenavService.setSidenavState(!this.data.closeSidenav);
        }

        // Set the page title in the browser tab if it exists
        if (this.data.title) {
          // Track page title with analyticsService. If the current page is a tab, get the breadcrumb else get the page title.
          this.analyticsService.trackDisplayPageEvent(this.translate.instant(this.data['breadCrumb'] ?? this.data.title));
          this.titleService.setTitle(this.translate.instant(this.data.title) + ' - myA');
        }
      });

    // Show new features notification if available
    this.notificationManager.showNewFeatureMessage();

    // Ping API to keep access token up to date as long as the user is active on the app
    this.idleService.ping$.pipe(
      switchMap(() => this.apiService.version$),
      takeUntilDestroyed(this.destroyRef)
    )
      .subscribe();

    // Log user out after app has been idle for some time
    this.idleService.timeout$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.authService.logout());

    this.authService.isAuthenticated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isAuthenticated => {
        if (isAuthenticated) {
          this.idleService.watch();
        } else {
          this.idleService.stop();
        }
      });
  }

  public ngOnDestroy(): void {
    this.mobileQuery.removeEventListener('change', this._mobileQueryListener);
  }
}
